import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import axios from 'axios';

interface Telecaller {
  id: string; // employee id
  employeeId?: string; // code/id from auth-service
  name: string;
  phone?: string | null;
  email?: string | null;
  position?: string | null;
}

interface Rider {
  id: string;
  name: string;
  phone: string;
  assignedTelecallerId?: string | null;
  assignedTelecaller?: {
    id: string;
    name: string;
    telecallerCode: string;
  } | null;
}

interface TelecallerAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  rider: Rider | null;
  // onAssignmentSuccess optionally receives assignedTelecaller and riderId so parent can update UI immediately
  onAssignmentSuccess: (assignedTelecaller?: any, riderId?: string) => void;
}

type AssignmentAction = 'assign' | 'reassign' | 'unassign';

const TelecallerAssignmentDialog: React.FC<TelecallerAssignmentDialogProps> = ({
  open,
  onClose,
  rider,
  onAssignmentSuccess,
}) => {
  const [telecallers, setTelecallers] = useState<Telecaller[]>([]);
  const [selectedTelecallerId, setSelectedTelecallerId] = useState<string>('');
  const [assignmentReason, setAssignmentReason] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingTelecallers, setFetchingTelecallers] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Determine the assignment action based on current rider state
  const getAssignmentAction = (): AssignmentAction => {
    if (!rider?.assignedTelecallerId) {
      return 'assign';
    }
    return selectedTelecallerId === '' ? 'unassign' : 'reassign';
  };

  // Fetch telecallers when dialog opens
  useEffect(() => {
    if (open) {
      fetchTelecallers();
      setError(null);
      setSuccess(null);
      // Use employee id if assigned via auth-service, otherwise fallback to assignedTelecallerId
      setSelectedTelecallerId((rider as any)?.employeeId || rider?.assignedTelecallerId || '');
      setAssignmentReason('');
    }
  }, [open, rider]);

  const fetchTelecallers = async () => {
    if (!rider) return;
    setFetchingTelecallers(true);
    setError(null);

    try {
      // Fetch telecallers available for this rider based on rider.city (use relative path so Vite dev proxy can route it)
      const response = await axios.get(`/api/telecaller-assignment/available/${rider.id}`, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success && Array.isArray(response.data.data?.telecallers)) {
        setTelecallers(response.data.data.telecallers);
      } else {
        setTelecallers([]);
        setError('No telecallers available for rider city');
      }
    } catch (err: any) {
      console.error('Error fetching telecallers for rider:', err);
      setError(err.response?.data?.message || 'Failed to load telecallers');
    } finally {
      setFetchingTelecallers(false);
    }
  };

  const handleAssign = async () => {
    if (!rider) return;

    const action = getAssignmentAction();

    // Validation
    if (action === 'assign' || action === 'reassign') {
      if (!selectedTelecallerId) {
        setError('Please select a telecaller');
        return;
      }
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let endpoint = '';
      let payload: any = {
        riderId: rider.id,
      };

      if (action === 'assign') {
        endpoint = `/api/telecaller-assignment/assign`;
        // Use employee-based assignment by sending telecallerEmployeeId
        payload.telecallerEmployeeId = selectedTelecallerId;
        payload.assignmentReason = assignmentReason || 'Initial assignment for supervision';
      } else if (action === 'reassign') {
        endpoint = `/api/telecaller-assignment/reassign`;
        payload.newTelecallerEmployeeId = selectedTelecallerId;
        payload.reassignmentReason =
          assignmentReason || `Reassigned to different telecaller`;
      } else if (action === 'unassign') {
        endpoint = `/api/telecaller-assignment/unassign`;
        payload.unassignmentReason = assignmentReason || 'Telecaller supervision no longer needed';
      }

      const response = await axios.post(endpoint, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.data.success) {
        setSuccess(response.data.message);

        // Prefer backend returned assigned telecaller; otherwise fall back to the selected telecaller from the fetched list
        let assignedTelecaller = response.data.data?.assignedTelecaller || null;
        if (!assignedTelecaller && selectedTelecallerId) {
          assignedTelecaller = telecallers.find((t) => t.id === selectedTelecallerId) || null;
        }

        setTimeout(() => {
          onAssignmentSuccess(assignedTelecaller, rider.id);
          handleClose();
        }, 1500);
      } else {
        setError(response.data.message || 'Assignment failed');
      }
    } catch (err: any) {
      console.error('Error assigning telecaller:', err);
      setError(err.response?.data?.message || 'Failed to update telecaller assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedTelecallerId('');
    setAssignmentReason('');
    setError(null);
    setSuccess(null);
    onClose();
  };

  const action = getAssignmentAction();
  const actionLabel =
    action === 'assign' ? 'Assign' : action === 'reassign' ? 'Reassign' : 'Unassign';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action === 'assign' && 'Assign Telecaller'}
        {action === 'reassign' && 'Reassign Telecaller'}
        {action === 'unassign' && 'Unassign Telecaller'}
      </DialogTitle>

      <DialogContent>
        {rider && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Rider Information
            </Typography>
            <Typography variant="body1">
              <strong>{rider.name}</strong> ({rider.phone})
            </Typography>

            {rider.assignedTelecaller && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={`Currently: ${rider.assignedTelecaller.name} (${rider.assignedTelecaller.telecallerCode})`}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {action !== 'unassign' && (
          <FormControl fullWidth sx={{ mb: 2 }} disabled={fetchingTelecallers || loading}>
            <InputLabel id="telecaller-select-label">
              {action === 'reassign' ? 'New Telecaller' : 'Telecaller'}
            </InputLabel>
            <Select
              labelId="telecaller-select-label"
              id="telecaller-select"
              value={selectedTelecallerId}
              label={action === 'reassign' ? 'New Telecaller' : 'Telecaller'}
              onChange={(e) => setSelectedTelecallerId(e.target.value)}
            >
              {fetchingTelecallers ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading telecallers...
                </MenuItem>
              ) : telecallers.length === 0 ? (
                <MenuItem disabled>No telecallers available</MenuItem>
              ) : (
                telecallers
                  .filter((tc) => tc.id !== rider?.assignedTelecallerId) // Exclude currently assigned telecaller id
                  .map((telecaller) => (
                    <MenuItem key={telecaller.id} value={telecaller.id}>
                      {telecaller.name} ({telecaller.employeeId || telecaller.id}) - {telecaller.phone || telecaller.email || 'No contact'}
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
        )}

        <TextField
          fullWidth
          label={`${actionLabel} Reason (Optional)`}
          multiline
          rows={3}
          value={assignmentReason}
          onChange={(e) => setAssignmentReason(e.target.value)}
          disabled={loading}
          placeholder={
            action === 'assign'
              ? 'e.g., New rider needs onboarding support'
              : action === 'reassign'
              ? 'e.g., Reassigning for better workload distribution'
              : 'e.g., Rider is now self-sufficient'
          }
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          color={action === 'unassign' ? 'error' : 'primary'}
          disabled={loading || fetchingTelecallers}
        >
          {loading ? <CircularProgress size={20} /> : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TelecallerAssignmentDialog;
